import React, { useState } from 'react';
import { AppBar, Toolbar, Button, Box, IconButton, Drawer, List, ListItem, Menu, MenuItem, Collapse, Typography } from '@mui/material';
import { Menu as MenuIcon, X as CloseIcon, Facebook, Instagram, Github, Mail, ChevronDown, ChevronUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [beyondMenuAnchor, setBeyondMenuAnchor] = useState<null | HTMLElement>(null);
  const [mobileBeyondOpen, setMobileBeyondOpen] = useState(false);
  const [mobileInterviewOpen, setMobileInterviewOpen] = useState(false);
  const [beyondInterviewOpen, setBeyondInterviewOpen] = useState(false);
  const [aiMenuAnchor, setAiMenuAnchor] = useState<null | HTMLElement>(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const scrollToSection = (sectionId: string, href: string) => {
    // If navigating to a specific route
    if (href === '/contact' || href === '/blog' || href === '/certification' || href === '/python-course' ||
        href === '/gen-ai-course' || href === '/gen-ai-course/enroll' || href === '/gen-ai-course/business-analyst' ||
        href === '/robotics-course' || href === '/data-science-course' || href === '/beyond-gallery' || href === '/live-events' || href === '/community-club' || href === '/internship-hub' || href === '/ai-training-dashboard' || href === '/our-creations' || href === '/job-ready-program' || href === '/interview-preparation/python') {
      navigate(href);
      setMobileOpen(false);
      return;
    }
    
    // If scrolling to a section on home (hero, beyond-the-classroom, etc.)
    if (sectionId && location.pathname !== '/') {
      navigate('/');
      const scrollToElement = () => {
        const element = document.getElementById(sectionId);
        if (element) {
          const navbarHeight = 52;
          const elementPosition = element.offsetTop - navbarHeight;
          window.scrollTo({
            top: elementPosition,
            behavior: 'smooth'
          });
          return true;
        }
        return false;
      };
      let attempts = 0;
      const maxAttempts = 20;
      const interval = setInterval(() => {
        attempts++;
        if (scrollToElement() || attempts >= maxAttempts) clearInterval(interval);
      }, 50);
      setMobileOpen(false);
      return;
    }
    
    // If we're on the home page, just scroll to the section
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        const navbarHeight = 52; // Approximate navbar height
        const elementPosition = element.offsetTop - navbarHeight;
        
        // Add a small delay for smooth animation
        setTimeout(() => {
          window.scrollTo({
            top: elementPosition,
            behavior: 'smooth'
          });
        }, 100);
      }
    } else {
      // If we're on a different page and trying to go to home, navigate first
      navigate('/');
      const scrollToElement = () => {
        const element = document.getElementById(sectionId);
        if (element) {
          const navbarHeight = 52;
          const elementPosition = element.offsetTop - navbarHeight;
          window.scrollTo({
            top: elementPosition,
            behavior: 'smooth'
          });
          return true;
        }
        return false;
      };
      
      // Try multiple times until element is found
      let attempts = 0;
      const maxAttempts = 20;
      const interval = setInterval(() => {
        attempts++;
        if (scrollToElement() || attempts >= maxAttempts) {
          clearInterval(interval);
        }
      }, 50);
    }
    setMobileOpen(false); // Close mobile drawer after navigation
  };

  // Sub-menu items under "Beyond the Classroom"
  const beyondSubItems = [
    { name: 'Interview Preparation', href: '', sectionId: '', children: [
      { name: 'Python', href: '/interview-preparation/python', sectionId: '' },
    ] },
    { name: 'Internship Hub', href: '/internship-hub', sectionId: '' },
    { name: 'Live Events and Program', href: '/live-events', sectionId: '' },
    { name: 'Beyond Gallery', href: '/beyond-gallery', sectionId: '' },
    { name: 'Community Club', href: '/community-club', sectionId: '', badge: 'Launching soon' },
  ];

  const aiSubItems = [
    { name: 'Gen AI Course', href: '/gen-ai-course', sectionId: '' },
    { name: 'AI Teacher Course', href: '/gen-ai-course/enroll', sectionId: '' },
    { name: 'Business Analyst Career Accelerator Program', href: '/gen-ai-course/business-analyst', sectionId: '' },
    { name: 'AI Training Dashboard', href: '/ai-training-dashboard', sectionId: '' },
  ];

  const navItems = [
    { name: 'Home', href: '#', sectionId: 'hero' },
    { name: 'Our Creations', href: '/our-creations', sectionId: '' },
    { name: 'Python', href: '/python-course', sectionId: '' },
    { name: 'AI for Everyone', href: '/gen-ai-course', sectionId: '' },
    { name: 'Robotics', href: '/robotics-course', sectionId: '' },
    { name: 'Blog', href: '/blog', sectionId: '' },
    { name: 'Contact Us', href: '/contact', sectionId: '' },
  ];

  const handleOpenBeyondMenu = (event: React.MouseEvent<HTMLElement>) => {
    setBeyondMenuAnchor(event.currentTarget);
  };

  const handleCloseBeyondMenu = () => {
    setBeyondMenuAnchor(null);
  };

  const handleOpenAiMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAiMenuAnchor(event.currentTarget);
  };

  const handleCloseAiMenu = () => {
    setAiMenuAnchor(null);
  };

  const drawer = (
    <Box sx={{ width: 280, bgcolor: '#FFFFFF', height: '100%', borderRight: '1px solid #E5E7EB' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: '1px solid #E5E7EB' }}>
            <Box
            component="img"
            src="/cropped-revinlogoyoutube-2.png"
            alt="Reviving India"
            onClick={() => {
              navigate('/');
              setMobileOpen(false);
            }}
            sx={{ 
              height: 44,
              width: 'auto',
              objectFit: 'contain',
              cursor: 'pointer',
              transition: 'opacity 0.2s',
              '&:hover': { opacity: 0.85 },
            }}
          />
        <IconButton 
          onClick={handleDrawerToggle}
          sx={{
            color: '#1E293B',
            '&:hover': { backgroundColor: '#EAF2FF', color: '#2563EB' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <List sx={{ py: 2, px: 1.5 }}>
        {/* Home (Mobile) */}
        {navItems.filter((i) => i.name === 'Home').map((item) => (
          <ListItem key={item.name} sx={{ py: 0.5, px: 1, mb: 1 }}>
            <Button
              onClick={() => scrollToSection(item.sectionId, item.href)}
              sx={{
                fontFamily: '"Poppins", "Inter", sans-serif',
                color: '#1E293B',
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 600,
                letterSpacing: '0.02em',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'flex-start',
                px: 2,
                py: 1.5,
                borderRadius: '12px',
                backgroundColor: 'transparent',
                border: '1px solid #E5E7EB',
                whiteSpace: 'nowrap',
                '&:hover': {
                  color: '#2563EB',
                  backgroundColor: '#EAF2FF',
                  borderColor: '#2563EB',
                },
              }}
            >
              {item.name}
            </Button>
          </ListItem>
        ))}
        {/* Python (Mobile) */}
        {navItems.filter((i) => i.name === 'Python').map((item) => (
          <ListItem key={item.name} sx={{ py: 0.5, px: 1, mb: 1 }}>
            <Button
              onClick={() => scrollToSection(item.sectionId, item.href)}
              sx={{
                fontFamily: '"Poppins", "Inter", sans-serif',
                color: '#1E293B',
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 600,
                letterSpacing: '0.02em',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'flex-start',
                px: 2,
                py: 1.5,
                borderRadius: '12px',
                backgroundColor: 'transparent',
                border: '1px solid #E5E7EB',
                whiteSpace: 'nowrap',
                '&:hover': { color: '#2563EB', backgroundColor: '#EAF2FF', borderColor: '#2563EB' },
              }}
            >
              {item.name}
            </Button>
          </ListItem>
        ))}
        {/* AI for Everyone - sub menu (Mobile) */}
        <ListItem sx={{ py: 0.5, px: 1, mb: 1 }}>
          <Typography sx={{ fontFamily: '"Poppins", "Inter", sans-serif', fontSize: '0.8rem', fontWeight: 600, color: '#64748B', px: 2, py: 0.5 }}>AI for Everyone</Typography>
        </ListItem>
        {aiSubItems.map((sub) => (
          <ListItem key={sub.name} sx={{ py: 0.5, px: 1, mb: 1, pl: 3 }}>
            <Button
              onClick={() => scrollToSection(sub.sectionId, sub.href)}
              sx={{
                fontFamily: '"Poppins", "Inter", sans-serif',
                color: '#1E293B',
                textTransform: 'none',
                fontSize: '0.9rem',
                fontWeight: 600,
                letterSpacing: '0.02em',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'flex-start',
                px: 2,
                py: 1.25,
                borderRadius: '12px',
                backgroundColor: 'transparent',
                border: '1px solid #E5E7EB',
                whiteSpace: 'nowrap',
                '&:hover': { color: '#2563EB', backgroundColor: '#EAF2FF', borderColor: '#2563EB' },
              }}
            >
              {sub.name}
            </Button>
          </ListItem>
        ))}
        {/* Robotics (Mobile) */}
        {navItems.filter((i) => i.name === 'Robotics').map((item) => (
          <ListItem key={item.name} sx={{ py: 0.5, px: 1, mb: 1 }}>
            <Button
              onClick={() => scrollToSection(item.sectionId, item.href)}
              sx={{
                fontFamily: '"Poppins", "Inter", sans-serif',
                color: '#1E293B',
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 600,
                letterSpacing: '0.02em',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'flex-start',
                px: 2,
                py: 1.5,
                borderRadius: '12px',
                backgroundColor: 'transparent',
                border: '1px solid #E5E7EB',
                whiteSpace: 'nowrap',
                '&:hover': { color: '#2563EB', backgroundColor: '#EAF2FF', borderColor: '#2563EB' },
              }}
            >
              {item.name}
            </Button>
          </ListItem>
        ))}

        {/* Beyond the Classroom (Mobile - collapsible: 4 sub-items) */}
        <ListItem sx={{ py: 0.5, px: 1, mb: 1 }}>
          <Button
            onClick={() => setMobileBeyondOpen(!mobileBeyondOpen)}
            endIcon={mobileBeyondOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            sx={{
              fontFamily: '"Poppins", "Inter", sans-serif',
              color: '#1E293B',
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 600,
              letterSpacing: '0.02em',
              display: 'flex',
              alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            px: 2,
            py: 1.5,
            borderRadius: '12px',
            backgroundColor: 'transparent',
            border: '1px solid #E5E7EB',
            whiteSpace: 'nowrap',
            '&:hover': {
              color: '#2563EB',
              backgroundColor: '#EAF2FF',
              borderColor: '#2563EB',
            },
          }}
        >
          Beyond the Classroom
        </Button>
        </ListItem>
        <Collapse in={mobileBeyondOpen} timeout="auto" unmountOnExit>
          <Box sx={{ pl: 1, pr: 1, pb: 1 }}>
            {beyondSubItems.map((item) => {
              const btnSx = {
                fontFamily: '"Poppins", "Inter", sans-serif',
                color: '#1E293B',
                textTransform: 'none' as const,
                fontSize: '0.9375rem',
                fontWeight: 600,
                letterSpacing: '0.02em',
                justifyContent: 'space-between',
                width: '100%',
                px: 2,
                py: 1.25,
                mb: 0.5,
                borderRadius: '10px',
                backgroundColor: 'transparent',
                '&:hover': { color: '#2563EB', backgroundColor: '#EAF2FF' },
              };

              if ('children' in item && item.children) {
                return (
                  <Box key={item.name}>
                    <Button
                      onClick={() => setMobileInterviewOpen((o) => !o)}
                      endIcon={mobileInterviewOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      sx={btnSx}
                    >
                      {item.name}
                    </Button>
                    <Collapse in={mobileInterviewOpen} timeout="auto" unmountOnExit>
                      <Box sx={{ pl: 2 }}>
                        {item.children.map((child) => (
                          <Button
                            key={child.name}
                            onClick={() => scrollToSection(child.sectionId, child.href)}
                            sx={{ ...btnSx, fontSize: '0.9rem', color: '#475569' }}
                          >
                            {child.name}
                          </Button>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                );
              }

              return (
                <Button
                  key={item.name}
                  onClick={() => scrollToSection(item.sectionId, item.href)}
                  sx={btnSx}
                >
                  {item.name}
                  {'badge' in item && item.badge && (
                    <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#2563EB', bgcolor: '#EAF2FF', px: 1.5, py: 0.25, borderRadius: '8px' }}>
                      {item.badge}
                    </Typography>
                  )}
                </Button>
              );
            })}
          </Box>
        </Collapse>

        {navItems.filter((i) => ['Our Creations', 'Blog', 'Contact Us'].includes(i.name)).map((item) => (
          <ListItem key={item.name} sx={{ py: 0.5, px: 1, mb: 1 }}>
            <Button
              onClick={() => scrollToSection(item.sectionId, item.href)}
              sx={{
                fontFamily: '"Poppins", "Inter", sans-serif',
                color: '#1E293B',
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 600,
                letterSpacing: '0.02em',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'flex-start',
                px: 2,
                py: 1.5,
                borderRadius: '12px',
                backgroundColor: 'transparent',
                border: '1px solid #E5E7EB',
                whiteSpace: 'nowrap',
                '&:hover': {
                  color: '#2563EB',
                  backgroundColor: '#EAF2FF',
                  borderColor: '#2563EB',
                },
              }}
            >
              {item.name}
            </Button>
          </ListItem>
        ))}
      </List>
      
      {/* Social Media Icons for Mobile */}
      <Box sx={{ 
        p: 2, 
        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
        display: 'flex',
        justifyContent: 'center',
        gap: 1
      }}>
        {[
          { Icon: Facebook, href: '#', color: '#1877F2', label: 'Facebook' },
          { Icon: Instagram, href: 'https://www.instagram.com/revivingindia/', color: '#E4405F', label: 'Instagram' },
          { Icon: Github, href: '#', color: '#333', label: 'GitHub' },
          { Icon: Mail, href: '#', color: '#DB4437', label: 'Gmail' }
        ].map(({ Icon, href, color, label }) => (
          <IconButton
            key={label}
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            sx={{
              color: color,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: `${color}20`,
                transform: 'scale(1.1)',
              },
            }}
          >
            <Icon size={24} />
          </IconButton>
        ))}
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="fixed" 
        className="navbar-modern"
        sx={{ 
          bgcolor: '#FFFFFF', 
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          borderBottom: '1px solid #E5E7EB',
          color: '#1E293B',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 1.5, md: 3 }, py: 0, minHeight: { xs: 48, md: 48 } }}>
          <motion.div
            style={{ flexShrink: 0 }}
            initial={{ x: -100, opacity: 0, scale: 0.8 }}
            animate={{ x: 0, opacity: 1, scale: 1 }}
            transition={{
              duration: 0.8,
              type: "spring",
              stiffness: 100,
              damping: 15
            }}
            whileHover={{
              scale: 1.05,
              transition: { duration: 0.2 }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: { xs: 0, md: 1 } }}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <Box
                  component="img"
                  src="/cropped-revinlogoyoutube-2.png"
                  alt="Reviving India"
                  sx={{ 
                    height: { xs: 40, md: 48 },
                    width: 'auto',
                    objectFit: 'contain',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      opacity: 0.8,
                    }
                  }}
                  onClick={() => navigate('/')}
                />
              </motion.div>
            </Box>
          </motion.div>

          <Box sx={{
            display: { xs: 'none', md: 'flex' },
            flexGrow: 1,
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'nowrap',
            gap: { md: 0.25, lg: 1 },
            mx: { md: 2, lg: 3 },
          }}>
            {navItems.filter((i) => i.name === 'Home').map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Button
                  onClick={() => scrollToSection(item.sectionId, item.href)}
                  sx={{
                    fontFamily: '"Poppins", "Inter", sans-serif',
                    color: '#1E293B',
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '12px',
                    px: 2,
                    py: 0.75,
                    minHeight: 'auto',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      color: '#2563EB',
                      backgroundColor: '#EAF2FF',
                    },
                  }}
                >
                  {item.name}
                </Button>
              </motion.div>
            ))}

            {/* Python (Desktop) */}
            {navItems.filter((i) => i.name === 'Python').map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: (index + 1) * 0.1 }}
              >
                <Button
                  onClick={() => scrollToSection(item.sectionId, item.href)}
                  sx={{
                    fontFamily: '"Poppins", "Inter", sans-serif',
                    color: '#1E293B',
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 0.75,
                    minHeight: 'auto',
                    borderRadius: '12px',
                    whiteSpace: 'nowrap',
                    '&:hover': { color: '#2563EB', backgroundColor: '#EAF2FF' },
                  }}
                >
                  {item.name}
                </Button>
              </motion.div>
            ))}
            {/* AI for Everyone dropdown (Desktop) */}
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              <Button
                onClick={handleOpenAiMenu}
                endIcon={<ChevronDown size={16} />}
                sx={{
                  fontFamily: '"Poppins", "Inter", sans-serif',
                  color: '#1E293B',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  py: 0.75,
                  minHeight: 'auto',
                  borderRadius: '12px',
                  whiteSpace: 'nowrap',
                  '&:hover': { color: '#2563EB', backgroundColor: '#EAF2FF' },
                }}
              >
                AI for Everyone
              </Button>
              <Menu
                anchorEl={aiMenuAnchor}
                open={Boolean(aiMenuAnchor)}
                onClose={handleCloseAiMenu}
                MenuListProps={{ 'aria-labelledby': 'ai-button' }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    bgcolor: '#FFFFFF',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04)',
                    border: '1px solid #E5E7EB',
                    borderRadius: '16px',
                    mt: 1.5,
                    minWidth: 220,
                    py: 0.5,
                  },
                }}
                transformOrigin={{ horizontal: 'center', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
              >
                {aiSubItems.map((sub) => (
                  <MenuItem
                    key={sub.name}
                    onClick={() => {
                      scrollToSection(sub.sectionId, sub.href);
                      handleCloseAiMenu();
                    }}
                    sx={{
                      fontFamily: '"Poppins", "Inter", sans-serif',
                      color: '#1E293B',
                      fontSize: '0.9375rem',
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      py: 1.25,
                    }}
                  >
                    {sub.name}
                  </MenuItem>
                ))}
              </Menu>
            </motion.div>
            {/* Robotics (Desktop) */}
            {navItems.filter((i) => i.name === 'Robotics').map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Button
                  onClick={() => scrollToSection(item.sectionId, item.href)}
                  sx={{
                    fontFamily: '"Poppins", "Inter", sans-serif',
                    color: '#1E293B',
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 0.75,
                    minHeight: 'auto',
                    borderRadius: '12px',
                    whiteSpace: 'nowrap',
                    '&:hover': { color: '#2563EB', backgroundColor: '#EAF2FF' },
                  }}
                >
                  {item.name}
                </Button>
              </motion.div>
            ))}

            {/* Beyond the Classroom Dropdown (Desktop: 4 sub-items) */}
            <motion.div
              key="Beyond the Classroom"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Button
                onClick={handleOpenBeyondMenu}
                endIcon={<ChevronDown size={16} />}
                sx={{
                  fontFamily: '"Poppins", "Inter", sans-serif',
                  color: '#1E293B',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  py: 0.75,
                  minHeight: 'auto',
                  borderRadius: '12px',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    color: '#2563EB',
                    backgroundColor: '#EAF2FF',
                  },
                }}
              >
                Beyond the Classroom
              </Button>
              <Menu
                anchorEl={beyondMenuAnchor}
                open={Boolean(beyondMenuAnchor)}
                onClose={handleCloseBeyondMenu}
                MenuListProps={{ 'aria-labelledby': 'beyond-button' }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    bgcolor: '#FFFFFF',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04)',
                    border: '1px solid #E5E7EB',
                    borderRadius: '16px',
                    mt: 1.5,
                    minWidth: 220,
                    py: 0.5,
                  }
                }}
                transformOrigin={{ horizontal: 'center', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
              >
                {beyondSubItems.map((item) => {
                  const menuItemSx = {
                    fontFamily: '"Poppins", "Inter", sans-serif',
                    color: '#1E293B',
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    py: 1.25,
                    px: 2.5,
                    mx: 0.75,
                    borderRadius: '10px',
                    mt: 0.25,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    '&:hover': { backgroundColor: '#EAF2FF', color: '#2563EB' },
                    '&:first-of-type': { mt: 0.5 },
                  } as const;

                  if ('children' in item && item.children) {
                    return (
                      <React.Fragment key={item.name}>
                        <MenuItem
                          onClick={(e) => { e.stopPropagation(); setBeyondInterviewOpen((o) => !o); }}
                          sx={menuItemSx}
                        >
                          {item.name}
                          {beyondInterviewOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </MenuItem>
                        {beyondInterviewOpen && item.children.map((child) => (
                          <MenuItem
                            key={child.name}
                            onClick={() => {
                              scrollToSection(child.sectionId, child.href);
                              handleCloseBeyondMenu();
                              setBeyondInterviewOpen(false);
                            }}
                            sx={{ ...menuItemSx, pl: 4.5, fontSize: '0.9rem', color: '#475569' }}
                          >
                            {child.name}
                          </MenuItem>
                        ))}
                      </React.Fragment>
                    );
                  }

                  return (
                    <MenuItem
                      key={item.name}
                      onClick={() => {
                        scrollToSection(item.sectionId, item.href);
                        handleCloseBeyondMenu();
                      }}
                      sx={menuItemSx}
                    >
                      {item.name}
                      {'badge' in item && item.badge && (
                        <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#2563EB', bgcolor: '#EAF2FF', px: 1.5, py: 0.25, borderRadius: '8px' }}>
                          {item.badge}
                        </Typography>
                      )}
                    </MenuItem>
                  );
                })}
              </Menu>
            </motion.div>

            {navItems.filter((i) => ['Our Creations', 'Blog', 'Contact Us'].includes(i.name)).map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: (index + 4) * 0.1 }}
              >
                <Button
                  onClick={() => scrollToSection(item.sectionId, item.href)}
                  sx={{
                    fontFamily: '"Poppins", "Inter", sans-serif',
                    color: '#1E293B',
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 0.75,
                    minHeight: 'auto',
                    borderRadius: '12px',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      color: '#2563EB',
                      backgroundColor: '#EAF2FF',
                    },
                  }}
                >
                  {item.name}
                </Button>
              </motion.div>
            ))}
          </Box>

          {/* Social Media Icons */}
          <Box sx={{
            display: { xs: 'none', lg: 'flex' },
            flexShrink: 0,
            gap: 1,
            alignItems: 'center',
            pl: 2,
            ml: 1,
            borderLeft: '1px solid #E5E7EB',
          }}>
            {[
              { Icon: Facebook, href: '#', color: '#1877F2', label: 'Facebook' },
              { Icon: Instagram, href: 'https://www.instagram.com/revivingindia/', color: '#E4405F', label: 'Instagram' },
              { Icon: Github, href: '#', color: '#333', label: 'GitHub' },
              { Icon: Mail, href: '#', color: '#DB4437', label: 'Gmail' }
            ].map(({ Icon, href, color, label }, index) => (
              <motion.div
                key={label}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              >
                <IconButton
                  component="a"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  sx={{
                    color: color,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: `${color}20`,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Icon size={20} />
                </IconButton>
              </motion.div>
            ))}
          </Box>

          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              display: { xs: 'block', md: 'none' },
              color: '#1E293B',
              '&:hover': {
                backgroundColor: '#EAF2FF',
              }
            }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 250,
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;
